import { ArrowForwardRounded, HighlightOff, SearchRounded } from '@mui/icons-material';
import { Box, InputAdornment, Stack, TextField } from '@mui/material';
import { type KeyboardEvent, useState } from 'react';

import { usePageTitle } from '../../hooks/usePageTitle';
import { type SearchOption, searchOption } from './SearchOption.types';
import { SearchOptionSelector } from './SearchOptionSelector';
import { ThemaSearch } from './ThemaSearch';
import { VeranstaltungSearch } from './VeranstaltungSearch';

export const SearchPage = () => {
  usePageTitle('');
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSearchOption, setSelectedSearchOption] = useState<SearchOption>(searchOption.THEMA);
  const searchTermTrimmed = searchTerm.trim();

  const updateSelectedSearchOption = (changedSearchOption: SearchOption): void => {
    setSelectedSearchOption(changedSearchOption);
    setInputValue(searchTerm);
  };

  const clearSearch = (): void => {
    setInputValue('');
    setSearchTerm('');
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Tab') {
      event.preventDefault();
      updateSelectedSearchOption(
        selectedSearchOption === searchOption.VERANSTALTUNG ? searchOption.THEMA : searchOption.VERANSTALTUNG,
      );
    } else if (event.key === 'Enter') {
      setSearchTerm(inputValue);
    } else if (event.key === 'Escape') {
      clearSearch();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(event.target.value);
  };

  const handleSearchIconClick = (): void => {
    setSearchTerm(inputValue);
  };

  const shouldDisplayClearIcon = inputValue.length > 0;

  return (
    <Box>
      <Stack alignItems="center" direction="column" sx={{ padding: 4, pt: 8, gap: 3 }}>
        <SearchOptionSelector selectedSearchOption={selectedSearchOption} onChange={updateSelectedSearchOption} />
        <TextField
          placeholder="Suche"
          autoFocus
          value={inputValue}
          onChange={handleChange}
          slotProps={{
            input: {
              onKeyDown,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {shouldDisplayClearIcon && <HighlightOff sx={{ cursor: 'pointer', mr: 1 }} onClick={clearSearch} />}
                  <ArrowForwardRounded color="primary" sx={{ cursor: 'pointer' }} onClick={handleSearchIconClick} />
                </InputAdornment>
              ),
            },
          }}
          sx={{ maxWidth: '90vw', width: '60em', background: 'white' }}
          variant="outlined"
        />
      </Stack>

      {selectedSearchOption === searchOption.VERANSTALTUNG && <VeranstaltungSearch searchTerm={searchTermTrimmed} />}
      {selectedSearchOption === searchOption.THEMA && <ThemaSearch searchTerm={searchTermTrimmed} />}
    </Box>
  );
};
